"use client";

import { WIDTH_MD } from "@constants/global";
import AppreciationResults from "components/RadarChart/AppreciationResultsChart";
import { Card, PageEmptyStateV2 } from "components/ui/v2";
import { useWindowWidth } from "@react-hook/window-size";
import type { UserVendorInfoRes } from "gql/User/queries";
import { LineChart } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import type { IAppreciationDistribute } from "types/Appreciation/AppreciationDistribute.type";
import type { IUser } from "types/User.types";
import { PublicProfileTab } from "./public-profile.constants";
import { PublicProfileEndorsementTabV2 } from "./endorsement/PublicProfileEndorsementTabV2";
import type { ISkill } from "types/Skills.type";
import { MoreAppreciationsV2 } from "./MoreAppreciationsV2";
import { ProjectsTabV2 } from "./ProjectsTabV2";
import { PublicRecognitionsTabV2 } from "./PublicRecognitionsTabV2";
import { UserCertificationsTabV2 } from "./UserCertificationsTabV2";

export interface PublicProfileTabPanelV2Props {
  activeTab: string;
  user: IUser;
  userId: string;
  selectedVendorId: string;
  userVendorInfo?: UserVendorInfoRes["userVendorInfo"];
  showingAppreciation?: IAppreciationDistribute;
  onAppreciationChange: (value: IAppreciationDistribute) => void;
  isOwnProfile: boolean;
  canShare: boolean;
  onEndorsementReview: (skill: ISkill) => void;
  onEndorsementValidate: (skillId: string, skillName: string) => void;
  onEndorsementShare: (skill: ISkill) => void;
  onSkillCountChange?: (count: number) => void;
}

export function PublicProfileTabPanelV2({
  activeTab,
  user,
  userId,
  selectedVendorId,
  userVendorInfo,
  showingAppreciation,
  onAppreciationChange,
  isOwnProfile,
  canShare,
  onEndorsementReview,
  onEndorsementValidate,
  onEndorsementShare,
  onSkillCountChange,
}: PublicProfileTabPanelV2Props) {
  const { t } = useTranslation("common");
  const wwidth = useWindowWidth();
  const isMd = wwidth >= WIDTH_MD;

  return (
    <Card size="full" tone="muted">
      {activeTab === PublicProfileTab.Endorsement && userVendorInfo ? (
        <PublicProfileEndorsementTabV2
          userId={userId}
          profileUserName={user.name}
          isOwnProfile={isOwnProfile}
          canShare={canShare}
          onReview={onEndorsementReview}
          onValidate={onEndorsementValidate}
          onShare={onEndorsementShare}
          onCountChange={onSkillCountChange}
        />
      ) : null}

      {activeTab === PublicProfileTab.Appreciation && userVendorInfo ? (
        <>
          {showingAppreciation ? (
            <div className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  {t("Última evaluación")}
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-zinc-400">
                  {showingAppreciation.appreciation?.name}
                </p>
              </div>
              {userVendorInfo.lastAppreciation !== null ? (
                <>
                  <MoreAppreciationsV2
                    userId={userId}
                    setAppreciationDistributes={onAppreciationChange}
                    selectedAppreciation={showingAppreciation}
                    vendorId={selectedVendorId}
                  />
                  <AppreciationResults
                    userMaturityLevelByDimension={
                      showingAppreciation.userMaturityLevelByDimension
                    }
                    showTitle
                    maxWidth={isMd ? 400 : 200}
                    userGeneralMaturity={showingAppreciation.userGeneralMaturityLevel}
                    borderRadious
                  />
                </>
              ) : null}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  {t("Última evaluación")}
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-zinc-400">
                  {t("No hay evaluaciones")}
                </p>
              </div>
              <PageEmptyStateV2
                variant="empty"
                icon={<LineChart className="h-7 w-7" />}
                title={t("Este no tiene evaluaciones para mostrar")}
                description={t(
                  "Habla con el líder de este usuario para enviarle una evaluación"
                )}
              />
            </div>
          )}
        </>
      ) : null}

      {activeTab === PublicProfileTab.Projects && userVendorInfo ? (
        <div className="space-y-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              {t("proyectos")}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-zinc-400">
              {t("Los proyectos en los que ha participado")}
            </p>
          </div>
          <ProjectsTabV2 userId={user.id} />
        </div>
      ) : null}

      {activeTab === PublicProfileTab.Certifications && userVendorInfo ? (
        <UserCertificationsTabV2 userCertificated={user} />
      ) : null}

      {activeTab === PublicProfileTab.Recognitions ? (
        <PublicRecognitionsTabV2 userId={userId} />
      ) : null}
    </Card>
  );
}
